import { supabase } from '@/lib/supabaseClient';
import { format, addHours, addMinutes } from 'date-fns';
import { notificationService } from './notificationService';

export interface BookingReminderData {
  user_id: string;
  title: string;
  message: string;
  metadata: Record<string, any>;
  link: string;
}

export const bookingReminderService = {
  /**
   * Schedule reminders for a booking
   */
  async scheduleReminders(
    bookingId: string,
    bookingType: 'booking' | 'booking_request',
    appointmentTime: Date,
    reminderData: BookingReminderData
  ): Promise<void> {
    try {
      // Skip host notifications if we don't have a valid user_id
      if (!reminderData.user_id) {
        return;
      }

      // Use database-driven reminder scheduling instead of setTimeout
      const { error } = await supabase.rpc('schedule_booking_reminders', {
        p_booking_id: bookingId,
        p_booking_type: bookingType,
        p_appointment_time: appointmentTime.toISOString(),
        p_notification_data: reminderData
      });

      if (error) {
        console.error('[bookingReminderService] Error scheduling database reminders:', error);
        throw error;
      }

    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule reminders:', error);
      throw error;
    }
  },

  /**
   * Cancel reminders for a booking
   */
  async cancelReminders(bookingId: string): Promise<void> {
    try {
      // Cancelling reminders for booking

      const { error } = await supabase.rpc('cancel_booking_reminders', {
        p_booking_id: bookingId
      });

      if (error) {
        console.error('[bookingReminderService] Error cancelling reminders:', error);
        throw error;
      }

      // Reminders cancelled successfully
    } catch (error) {
      console.error('[bookingReminderService] Failed to cancel reminders:', error);
      throw error;
    }
  },

  /**
   * Get reminder data for a booking
   */
  getReminderData(
    bookingData: any,
    bookingType: 'booking' | 'booking_request',
    appointmentTime: Date
  ): BookingReminderData {
    const isBookingRequest = bookingType === 'booking_request';
    
    // Get timezone information
    let timezoneInfo = '';
    let timezoneAbbr = '';
    
    // Try to get timezone from booking data
    if (bookingData.extra_information?.webhookData?.timezone) {
      const timezone = bookingData.extra_information.webhookData.timezone;
      timezoneAbbr = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(appointmentTime).find(part => part.type === 'timeZoneName')?.value || '';
      timezoneInfo = ` (${timezoneAbbr})`;
    } else if (bookingData.extra_information?.webhookData?.timezone_friendly) {
      timezoneInfo = ` (${bookingData.extra_information.webhookData.timezone_friendly})`;
    } else {
      // Fallback to user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      timezoneAbbr = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'short'
      }).formatToParts(appointmentTime).find(part => part.type === 'timeZoneName')?.value || '';
      timezoneInfo = ` (${timezoneAbbr})`;
    }
    
    // Format appointment time with timezone
    const formattedTime = format(appointmentTime, 'EEEE, MMMM do, yyyy \'at\' h:mm a') + timezoneInfo;
    
    // Create reminder messages with meeting link
    let oneHourMessage = `Your appointment is scheduled to start in 1 hour ${formattedTime}. Please be ready for your call.`;
    let fiveMinMessage = `Your appointment is starting in 5 minutes ${formattedTime}. Please join the meeting now.`;
    
    // Add meeting link to messages if available
    if (bookingData.meeting_link) {
      oneHourMessage += `\n\nMeeting Link: ${bookingData.meeting_link}`;
      fiveMinMessage += `\n\nMeeting Link: ${bookingData.meeting_link}`;
    }

    // Create calendar URL for team members
    let calendarUrl = 'https://go.solerro.com/app/scheduler/appointments';
    const params = new URLSearchParams();
    
    if (isBookingRequest) {
      if (bookingData.booking_link_owner_id) {
        params.set('showAllBookings', 'true');
        params.set('assignedUser', bookingData.booking_link_owner_id);
      }
      if (bookingData.id) {
        params.set('bookingId', bookingData.id);
      }
    } else {
      // For regular bookings
      if (bookingData.event?.user_id) {
        params.set('showAllBookings', 'true');
        params.set('assignedUser', bookingData.event.user_id);
      }
      if (bookingData.id) {
        params.set('bookingId', bookingData.id);
      }
    }
    
    if (params.toString()) {
      calendarUrl += `?${params.toString()}`;
    }

    // Process custom field responses for template variables (for booking requests)
    let customQuestionsFormatted = '';
    let customQuestionsPlain = '';
    if (isBookingRequest && bookingData.custom_field_responses && Object.keys(bookingData.custom_field_responses).length > 0) {
      // Get the custom fields configuration from the booking link to get proper labels
      const customFieldsMap = new Map();
      if (bookingData.booking_link?.custom_fields && bookingData.booking_link.custom_fields.length > 0) {
        bookingData.booking_link.custom_fields.forEach((field: any) => {
          customFieldsMap.set(field.id, field.label);
        });
      }

      const customQuestionsArray: string[] = [];
      const customQuestionsPlainArray: string[] = [];
      
      Object.entries(bookingData.custom_field_responses).forEach(([fieldId, value]) => {
        if (value && value.toString().trim()) {
          const label = customFieldsMap.get(fieldId) || fieldId;
          customQuestionsArray.push(`<strong>${label}:</strong> ${value}`);
          customQuestionsPlainArray.push(`${label}: ${value}`);
        }
      });
      
      if (customQuestionsArray.length > 0) {
        customQuestionsFormatted = '<br/><br/><strong>Additional Information:</strong><br/>' + customQuestionsArray.join('<br/>');
        customQuestionsPlain = '\n\nAdditional Information:\n' + customQuestionsPlainArray.join('\n');
      }
    }

    // Format date and time for template variables
    const formattedDate = format(appointmentTime, 'EEEE, MMMM do, yyyy');
    const formattedTimeShort = format(appointmentTime, 'h:mm a') + timezoneInfo;

    // Create metadata
    const metadata: Record<string, any> = {
      booking_id: bookingData.id,
      appointment_time: appointmentTime.toISOString(),
      booking_type: bookingType,
      calendar_url: calendarUrl,
      // Template variables for booking reminder notifications
      booking_date: formattedDate,
      booking_time: formattedTimeShort,
      customer_name: isBookingRequest ? bookingData.requester_name : `${bookingData.first_name} ${bookingData.last_name}`,
      project_name: isBookingRequest ? (bookingData.booking_link?.name || 'Appointment') : (bookingData.event?.name || 'Meeting'),
      meeting_link: bookingData.meeting_link || '',
      // Custom questions formatted for templates
      custom_questions_html: customQuestionsFormatted,
      custom_questions_text: customQuestionsPlain,
      has_custom_questions: customQuestionsFormatted ? 'true' : 'false'
    };

    if (isBookingRequest) {
      metadata.booking_request_id = bookingData.id;
      metadata.requester_name = bookingData.requester_name;
      metadata.requester_email = bookingData.requester_email;
      metadata.requested_date = bookingData.requested_date;
      metadata.requested_time_slot = bookingData.requested_time_slot;
      metadata.booking_link_name = bookingData.booking_link?.name;
      metadata.booking_link_owner_id = bookingData.booking_link?.user_id;
    } else {
      metadata.customer_email = bookingData.email;
      metadata.event_name = bookingData.event?.name;
    }

    // Ensure we have a valid user_id, otherwise skip host notifications
    const userId = isBookingRequest ? bookingData.booking_link?.user_id : bookingData.event?.user_id;
    
    return {
      user_id: userId,
      title: `Appointment Reminder: ${isBookingRequest ? bookingData.requester_name : `${bookingData.first_name} ${bookingData.last_name}`}`,
      message: oneHourMessage, // This will be overridden for 5min reminders
      metadata,
      link: calendarUrl
    };
  },

  /**
   * Schedule reminders for a booking request
   */
  async scheduleBookingRequestReminders(bookingRequest: any): Promise<void> {
    try {
      // Parse appointment time
      const appointmentTime = new Date(`${bookingRequest.requested_date}T${bookingRequest.requested_time_slot}`);
      
      // Get reminder data for team members (links to calendar)
      const teamReminderData = this.getReminderData(bookingRequest, 'booking_request', appointmentTime);
      
      // Schedule reminders for team members
      await this.scheduleReminders(
        bookingRequest.id,
        'booking_request',
        appointmentTime,
        teamReminderData
      );
      
      // Also schedule reminders for the customer (person who booked) if they have an account
      await this.scheduleBookingRequestCustomerReminders(bookingRequest, appointmentTime);
    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule booking request reminders:', error);
      throw error;
    }
  },

  /**
   * Schedule reminders for the customer (person who booked a booking request)
   */
  async scheduleBookingRequestCustomerReminders(bookingRequest: any, appointmentTime: Date): Promise<void> {
    try {
      // Try to find the customer by email
      const { data: customer, error: customerError } = await supabase
        .from('users')
        .select('id')
        .eq('email', bookingRequest.requester_email)
        .maybeSingle();

      if (customerError || !customer?.id) {
        
        // Schedule direct email reminders for non-user customers
        try {
          await this.scheduleDirectEmailReminders(bookingRequest, appointmentTime);
        } catch (error) {
          console.error('[bookingReminderService] Error scheduling direct email reminders:', error);
        }
        return;
      }

      // Get booking link URL for the customer
      let bookingLinkUrl = '';
      try {
        const { data: bookingLink, error: linkError } = await supabase
          .from('booking_links')
          .select('slug')
          .eq('id', bookingRequest.booking_link_id)
          .single();

        if (!linkError && bookingLink) {
          bookingLinkUrl = `https://go.solerro.com/book/${bookingLink.slug}`;
        }
      } catch (error) {
        console.warn('[bookingReminderService] Error fetching booking link for customer reminders:', error);
      }

      // Get timezone information
      let timezoneInfo = '';
      if (bookingRequest.extra_information?.webhookData?.timezone) {
        const timezone = bookingRequest.extra_information.webhookData.timezone;
        const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'short'
        }).formatToParts(appointmentTime).find(part => part.type === 'timeZoneName')?.value || '';
        timezoneInfo = ` (${timezoneAbbr})`;
      } else if (bookingRequest.extra_information?.webhookData?.timezone_friendly) {
        timezoneInfo = ` (${bookingRequest.extra_information.webhookData.timezone_friendly})`;
      } else {
        // Default to Mountain Time for this user
        timezoneInfo = ' (MST)';
      }

      // Format appointment time with timezone
      const formattedTime = format(appointmentTime, 'EEEE, MMMM do, yyyy \'at\' h:mm a') + timezoneInfo;
      
      // Create reminder messages with meeting link
      let oneHourMessage = `Your appointment is scheduled to start in 1 hour ${formattedTime}. Please be ready for your call.`;
      let fiveMinMessage = `Your appointment is starting in 5 minutes ${formattedTime}. Please join the meeting now.`;
      
      // Add meeting link to messages if available
      if (bookingRequest.meeting_link) {
        oneHourMessage += `\n\nMeeting Link: ${bookingRequest.meeting_link}`;
        fiveMinMessage += `\n\nMeeting Link: ${bookingRequest.meeting_link}`;
      }

      // Create customer reminder data
      const customerReminderData: BookingReminderData = {
        user_id: customer.id,
        title: `Appointment Reminder: ${bookingRequest.booking_link?.name || 'Site Survey'}`,
        message: oneHourMessage,
        metadata: {
          booking_request_id: bookingRequest.id,
          appointment_time: appointmentTime.toISOString(),
          booking_type: 'booking_request',
          customer_name: bookingRequest.requester_name,
          customer_email: bookingRequest.requester_email,
          event_name: bookingRequest.booking_link?.name,
          meeting_link: bookingRequest.meeting_link,
          booking_link_url: bookingLinkUrl,
          is_customer_reminder: true
        },
        link: bookingRequest.meeting_link || bookingLinkUrl || `https://go.solerro.com/app/scheduler/appointments?showAllBookings=true&assignedUser=${customer.id}&bookingId=${bookingRequest.id}`
      };

      // Schedule customer reminders
      await this.scheduleReminders(
        bookingRequest.id,
        'booking_request',
        appointmentTime,
        customerReminderData
      );

      // Customer reminders scheduled for booking request
    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule customer reminders for booking request:', error);
      // Don't fail the main booking if customer reminders fail
    }
  },

  /**
   * Schedule reminders for a regular booking
   */
  async scheduleBookingReminders(booking: any): Promise<void> {
    try {
      // Parse appointment time
      const appointmentTime = new Date(booking.start_time);
      
      // Get reminder data for team members (links to calendar)
      const teamReminderData = this.getReminderData(booking, 'booking', appointmentTime);
      
      // Schedule reminders for team members
      await this.scheduleReminders(
        booking.id,
        'booking',
        appointmentTime,
        teamReminderData
      );
      
      // Also schedule reminders for the customer (person who booked) if they have an account
      await this.scheduleCustomerReminders(booking, appointmentTime);
    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule booking reminders:', error);
      throw error;
    }
  },

  /**
   * Schedule reminders for the customer (person who booked)
   */
  async scheduleCustomerReminders(booking: any, appointmentTime: Date): Promise<void> {
    try {
      // Try to find the customer by email
      const { data: customer, error: customerError } = await supabase
        .from('users')
        .select('id')
        .eq('email', booking.email)
        .maybeSingle();

      if (customerError || !customer?.id) {
        // Customer not found in system, skipping customer reminders
        return;
      }

      // Get booking link URL for the customer
      // For regular bookings, we need to find the booking link through booking requests
      let bookingLinkUrl = '';
      try {
        // First, try to find a booking request that was created from this booking
        const { data: bookingRequest, error: requestError } = await supabase
          .from('booking_requests')
          .select(`
            booking_link:booking_links (
              slug
            )
          `)
          .eq('booking_id', booking.id)
          .single();

        if (!requestError && bookingRequest?.booking_link?.[0]) {
          bookingLinkUrl = `https://go.solerro.com/book/${bookingRequest.booking_link[0].slug}`;
        } else {
          // If no booking request found, try to find a booking link by the event owner
          const { data: eventData, error: eventError } = await supabase
            .from('events')
            .select('user_id')
            .eq('id', booking.event?.id)
            .single();

          if (!eventError && eventData) {
            const { data: bookingLinks, error: linkError } = await supabase
              .from('booking_links')
              .select('slug')
              .eq('user_id', eventData.user_id)
              .limit(1);

            if (!linkError && bookingLinks && bookingLinks.length > 0) {
              bookingLinkUrl = `https://go.solerro.com/book/${bookingLinks[0].slug}`;
            }
          }
        }
      } catch (error) {
        console.warn('[bookingReminderService] Error fetching booking link for customer reminders:', error);
      }

      // Get timezone information
      let timezoneInfo = '';
      if (booking.extra_information?.webhookData?.timezone) {
        const timezone = booking.extra_information.webhookData.timezone;
        const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'short'
        }).formatToParts(appointmentTime).find(part => part.type === 'timeZoneName')?.value || '';
        timezoneInfo = ` (${timezoneAbbr})`;
      } else if (booking.extra_information?.webhookData?.timezone_friendly) {
        timezoneInfo = ` (${booking.extra_information.webhookData.timezone_friendly})`;
      } else {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZone: userTimezone,
          timeZoneName: 'short'
        }).formatToParts(appointmentTime).find(part => part.type === 'timeZoneName')?.value || '';
        timezoneInfo = ` (${timezoneAbbr})`;
      }

      // Format appointment time with timezone
      const formattedTime = format(appointmentTime, 'EEEE, MMMM do, yyyy \'at\' h:mm a') + timezoneInfo;
      
      // Create reminder messages with meeting link
      let oneHourMessage = `Your appointment is scheduled to start in 1 hour ${formattedTime}. Please be ready for your call.`;
      let fiveMinMessage = `Your appointment is starting in 5 minutes ${formattedTime}. Please join the meeting now.`;
      
      // Add meeting link to messages if available
      if (booking.meeting_link) {
        oneHourMessage += `\n\nMeeting Link: ${booking.meeting_link}`;
        fiveMinMessage += `\n\nMeeting Link: ${booking.meeting_link}`;
      }

      // Create customer reminder data
      const customerReminderData: BookingReminderData = {
        user_id: customer.id,
        title: `Appointment Reminder: ${booking.event?.name || 'Site Survey'}`,
        message: oneHourMessage,
        metadata: {
          booking_id: booking.id,
          appointment_time: appointmentTime.toISOString(),
          booking_type: 'booking',
          customer_name: `${booking.first_name} ${booking.last_name}`,
          customer_email: booking.email,
          event_name: booking.event?.name,
          meeting_link: booking.meeting_link,
          booking_link_url: bookingLinkUrl,
          is_customer_reminder: true
        },
        link: booking.meeting_link || bookingLinkUrl || `https://go.solerro.com/app/scheduler/appointments?showAllBookings=true&assignedUser=${customer.id}&bookingId=${booking.id}`
      };

      // Schedule customer reminders
      await this.scheduleReminders(
        booking.id,
        'booking',
        appointmentTime,
        customerReminderData
      );

      // Customer reminders scheduled
    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule customer reminders:', error);
      // Don't fail the main booking if customer reminders fail
    }
  },

  /**
   * Update reminders when a booking is rescheduled
   */
  async updateReminders(
    bookingId: string,
    bookingType: 'booking' | 'booking_request',
    newAppointmentTime: Date,
    reminderData: BookingReminderData
  ): Promise<void> {
    try {
      // Cancel existing reminders
      await this.cancelReminders(bookingId);
      
      // Schedule new reminders
      await this.scheduleReminders(bookingId, bookingType, newAppointmentTime, reminderData);
      
      // Reminders updated successfully
    } catch (error) {
      console.error('[bookingReminderService] Failed to update reminders:', error);
      throw error;
    }
  },

  /**
   * Schedule direct email reminders for customers who aren't users in the system
   */
  async scheduleDirectEmailReminders(bookingRequest: any, appointmentTime: Date): Promise<void> {
    try {
      // Get booking link details for the email
      let bookingLinkName = 'Your Appointment';
      let customQuestionsFormatted = '';
      let customQuestionsPlain = '';
      const bookedWithName = '';
      
      try {
        const { data: bookingLink, error: linkError } = await supabase
          .from('booking_links')
          .select('name, custom_fields')
          .eq('id', bookingRequest.booking_link_id)
          .single();

        if (!linkError && bookingLink) {
          bookingLinkName = bookingLink.name;
          
          // Process custom field responses for email
          if (bookingRequest.custom_field_responses && Object.keys(bookingRequest.custom_field_responses).length > 0) {
            const customFieldsMap = new Map();
            if (bookingLink.custom_fields && bookingLink.custom_fields.length > 0) {
              bookingLink.custom_fields.forEach((field: any) => {
                customFieldsMap.set(field.id, field.label);
              });
            }

            const customQuestionsArray: string[] = [];
            const customQuestionsPlainArray: string[] = [];
            Object.entries(bookingRequest.custom_field_responses).forEach(([fieldId, value]) => {
              if (value && value.toString().trim()) {
                const label = customFieldsMap.get(fieldId) || fieldId;
                customQuestionsArray.push(`<strong>${label}:</strong> ${value}`);
                customQuestionsPlainArray.push(`${label}: ${value}`);
              }
            });
            
            if (customQuestionsArray.length > 0) {
              customQuestionsFormatted = '<br/><br/><strong>Additional Information:</strong><br/>' + customQuestionsArray.join('<br/>');
              customQuestionsPlain = '\n\nAdditional Information:\n' + customQuestionsPlainArray.join('\n');
            }
          }
        }
      } catch (error) {
        console.warn('[bookingReminderService] Error fetching booking link for email reminders:', error);
      }

      // Format appointment time
      const formattedDate = format(appointmentTime, 'EEEE, MMMM do, yyyy');
      const formattedTime = format(appointmentTime, 'h:mm a');
      const formattedDateTime = `${formattedDate} at ${formattedTime}`;

      // Calculate reminder times
      const oneHourBefore = new Date(appointmentTime.getTime() - 60 * 60 * 1000);
      const fiveMinutesBefore = new Date(appointmentTime.getTime() - 5 * 60 * 1000);

      // Import emailService for direct email sending
      const { emailService } = await import('./emailService');

      // Create reminder data for the customer and invited attendees
      const customerReminderData: BookingReminderData = {
        user_id: 'external_customer', // Special marker for external customers
        title: `Appointment Reminder: ${bookingLinkName}`,
        message: `Hello ${bookingRequest.requester_name}, your appointment "${bookingLinkName}" is scheduled for ${formattedDateTime}.`,
        metadata: {
          customer_email: bookingRequest.requester_email,
          customer_name: bookingRequest.requester_name,
          booking_date: formattedDate,
          booking_time: formattedTime,
          project_name: bookingLinkName,
          meeting_link: bookingRequest.meeting_link || '',
          custom_questions_html: customQuestionsFormatted,
          custom_questions_text: customQuestionsPlain,
          has_custom_questions: customQuestionsFormatted ? 'true' : 'false',
          host_name: bookedWithName || 'Solerro Team',
          invite_emails: bookingRequest.invite_emails || [],
          is_external_customer: true
        },
        link: bookingRequest.meeting_link || ''
      };

      // Use database-driven reminder scheduling for external customers
      const { error } = await supabase.rpc('schedule_booking_reminders', {
        p_booking_id: bookingRequest.id,
        p_booking_type: 'booking_request',
        p_appointment_time: appointmentTime.toISOString(),
        p_notification_data: customerReminderData
      });

      if (error) {
        console.error('[bookingReminderService] Error scheduling external customer reminders:', error);
        // Log but don't fail the main booking process
      } else {
      }

    } catch (error) {
      console.error('[bookingReminderService] Failed to schedule direct email reminders:', error);
      throw error;
    }
  }
}; 